<template>

<div class="dropdown">
    <div class="d-flex" ref="dropdownRef" v-click-outside="onClickOutside">
        <slot name="button">
          <button 
              :class="props.btnClass" 
              type="button" 
              :aria-expanded="showing"
              @click="toggleDropdown()"
          >
              <slot name="label">
                  <span v-html="props.label"></span>
              </slot>
          </button>
        </slot>
        <Transition :name="props.transitionName">
            <div 
                v-if="showing"
                class="dropwrap" 
                ref="dropMenu"
            >
                <slot name="dropmenu">
                    <ul :class="[props.dropClass, { 'show': showing }]" >
                        <li 
                            v-for="row in options" 
                            :key="row.label" 
                            :class="(row.css || 'dropdown-item')"
                            @click="$emit('select', row)"
                        >
                            <slot name="dropitem">
                                <div v-html="row.label"></div>
                            </slot>
                        </li>
                    </ul>
                </slot>
            </div>
        </Transition>
    </div>
</div>


</template>

<script lang="ts" setup>

/**
 * @name KtDropmenu
 * @description A flexible dropdown menu component.
 * @slot button - The button that triggers the dropdown.
 * @slot label - The label for the button.
 * @slot dropmenu - The dropdown menu itself.
 * @slot dropitem - The individual items in the dropdown menu.
 */

import { ref, watch, nextTick } from "vue";
import type { DropmenuOption } from "./KtDropmenu";

/**
 * Custom directive to detect clicks outside an element.
 */
const vClickOutside = {
  beforeMount: (el: any, binding: any) => {
    el.clickOutsideEvent = (event: MouseEvent) => {
      // here I check that click was outside the el and his children
      if (!(el == event.target || el.contains(event.target))) {
        // and if it did, call method provided in attribute value
        binding.value();
      }
    };
    document.addEventListener("click", el.clickOutsideEvent);
  },
  unmounted: (el: any) => {
    document.removeEventListener("click", el.clickOutsideEvent);
  },
};

/**
 * @model visible
 * @description Controls the visibility of the dropdown menu from the parent component.
 */
const visible = defineModel<boolean>("visible", {
    default: false
});

const props = defineProps({
    
    /**
     * @prop btnClass
     * @description The CSS class for the button.
     */
    btnClass: {
        type: String,
        default: () => "btn btn-outline-secondary dropdown-toggle"
    },
    
    /**
     * @prop dropClass
     * @description The CSS class for the dropdown menu.
     */
    dropClass: {
        type: String,
        default: () => "dropdown-menu"
    },

    /**
     * @prop label
     * @description The label for the button.
     */
    label: {
        type: String,
        default: () => "Select"
    },

    /**
     * @prop options
     * @description The options to display in the dropdown menu.
     */
    options: {
        type: Array<DropmenuOption>,
        default: () => ([])
    },

    /**
     * @prop transitionName
     * @description The name of the transition to use for the dropdown menu.
     */
    transitionName: {
        type: String,
        default: ''
    }

});

const emits = defineEmits<{
    /**
     * @event select
     * @description Emitted when an option is selected.
     * @param {any} value - The selected value.
     */
    (e: 'select', value: any):void
}>();

// A ref to the dropdown container for detecting outside clicks.
const dropdownRef = ref<HTMLElement | null>(null);

// A ref to the dropdown menu element.
const dropMenu = ref<HTMLElement | null>(null);

// The internal state for showing/hiding the dropdown menu.
const showing = ref<boolean>(false);

/**
 * @function toggleDropdown
 * @description Toggles the visibility of the dropdown menu.
 */
const toggleDropdown = () => 
{
    showing.value = !showing.value;
};

/**
 * @function onClickOutside
 * @description Closes the dropdown menu when a click is detected outside of it.
 */
const onClickOutside = () => {
    showing.value = false;
};

// Watch for changes to the visible model from the parent and sync the internal state.
watch(() => visible.value, (newValue) => {
    showing.value = newValue;
});

// Watch for changes in the internal showing state and update the parent model and handle focus.
watch(showing, (newValue: boolean) => {
    // Update the parent's v-model if it's out of sync.
    if (visible.value !== newValue) {
        visible.value = newValue;
    }

    // If the menu is being shown, focus it.
    if (newValue) {
        nextTick(() => {
            dropMenu.value?.focus();
        });
    }
});

</script>